import { useEffect, useState, type ReactNode } from 'react'
import { Icon } from './icons'
import './notifications.css'

export type NotificationLevel = 'info' | 'success' | 'warning' | 'error'
export interface TableNotification {
  /** Stable identity for an ongoing condition. Remove it when that condition resolves. */
  readonly id: string
  readonly level: NotificationLevel
  /** Routine info/success defaults to the turn chip; warnings/errors to the center. */
  readonly placement?: 'status' | 'alert'
  readonly title: string
  readonly message?: string
  readonly busy?: boolean
  readonly retryAt?: number
  readonly content?: ReactNode
  readonly actions?: ReactNode
  /** Omit for persistent conditions that must remain visible until resolved. */
  readonly onDismiss?: () => void
}
const priority: Record<NotificationLevel, number> = { error: 0, warning: 1, success: 2, info: 3 }

function RetryTime({ at }: { at: number }) {
  const [now, setNow] = useState(Date.now)
  useEffect(() => {
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [at])
  const seconds = Math.max(0, Math.ceil((at - now) / 1000))
  return <span className="tt-notification-retry" aria-live="off">{seconds > 0
    ? `Next automatic retry in ${seconds >= 60 ? `${Math.floor(seconds / 60)}m ` : ''}${seconds % 60}s.`
    : 'Waiting for the automatic retry…'}</span>
}

/** One top-of-table feed. State updates replace alerts instead of stacking duplicates. */
export function NotificationFeed({ notifications, children, compact = false }: { notifications: readonly TableNotification[]; children?: ReactNode; compact?: boolean }) {
  return <section className={`tt-notification-feed${compact ? ' is-compact' : ''}`} aria-label={compact ? 'Table updates' : 'Notifications'}>
    {[...notifications].sort((a, b) => priority[a.level] - priority[b.level]).map(notification => <article
      key={notification.id} className={`tt-notification is-${notification.level}`} data-notification={notification.id} data-level={notification.level}>
      <span className="tt-notification-icon" aria-hidden="true">{notification.busy ? <span className="tt-notification-spinner"/> : notification.level === 'warning' || notification.level === 'error' ? '!' : <Icon name={notification.level === 'success' ? 'check' : 'bell'} size={16}/>}</span>
      <div className="tt-notification-body">
        <div role={notification.level === 'error' ? 'alert' : 'status'} aria-atomic="true">
          <span className="tt-sr-only">{notification.level}: </span><strong>{notification.title}</strong>
          {notification.message && <p>{notification.message}</p>}
        </div>
        {notification.retryAt !== undefined && <RetryTime at={notification.retryAt}/>}
        {notification.content}
        {notification.actions && <div className="tt-notification-actions">{notification.actions}</div>}
      </div>
      {notification.onDismiss && <button type="button" className="tt-icon-button" aria-label={`Dismiss ${notification.title}`} onClick={notification.onDismiss}><Icon name="close" size={14}/></button>}
    </article>)}
    {children}
  </section>
}
